import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter } from '@reduxjs/toolkit'
import getUsageData from "../../functions/getUsageDataAPI"
import { getAssociatedResourcesDictionary } from "../../functions/getResourceReferences"
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice"

//This one is different from the other one that's defined in the resourceCollectionSlice
const fetchResourceCollectionAPI = async (parentProductFoi, productFamily, apiToken, resourceType, fhirJson) => {

    let resourceEntities = [];
    let resourceIdList = [];
    let resource = fhirJson;
    let entity = {
        id: resource.id,
        displayTitle: resource.title ||
            (typeof resource.name === "string" && resource.name) ||
            (Array.isArray(resource.name) && resource.name[0].text) ||
            "Unnamed " + resource.resourceType,
        productFamily: productFamily, parentProductFOI: parentProductFoi,
        resourceType: resource.resourceType,
        resourceJson: resource,
        revisedResourceJson: resource,
        usageData: {}
    };
    resourceIdList.push(resource.id);
    resourceEntities.push(entity);

    //associatedDictionary[resourceType].push({ "title": string, "url": string, "foi": string, "fli": string });
    let associatedResourcesDictionary = getAssociatedResourcesDictionary(fhirJson);
    for (const resourceType in associatedResourcesDictionary) {
        const associatedResources = associatedResourcesDictionary[resourceType];
        for (const associatedResource of associatedResources) {
            if (associatedResource.foi && !isNaN(associatedResource.foi)) {
                resourceIdList.push(associatedResource.foi);
                let entity = {
                    id: associatedResource.foi,
                    displayTitle: associatedResource.title || ("Unnamed " + resourceType),
                    productFamily: productFamily, parentProductFOI: parentProductFoi,
                    resourceType: resourceType,
                    resourceJson: {},
                    revisedResourceJson: {},
                    usageData: {},
                    invitecode: ""
                };
                resourceEntities.push(entity);
            }
        }
    }

    //usageDataDictionary contains {totalviews: 0, totaledits: 0, totalusersviewed: 0, 
    //   totalusersedited: 0, editpermission: boolean, readpermission: boolean,
    //  adminpermission: boolean}
    let usageDataDictionary = await getUsageData(resourceIdList, apiToken);
    for (let entity of resourceEntities) {
        entity.usageData = usageDataDictionary[entity.id];
    }
    return resourceEntities;
}


//entity: { id: "FOI", displayTitle: "", productFamily: "", parentProductFOI: "", 
//          resourceType: "", resourceJson: {}, revisedResourceJson: {}, usageData: {} }
//key uses: Builder/Viewer Tools (to have access to full JSON of referenced Resources);
//  initially pull the set from FEvIR when the FOI/productFamily combination can identify a relevant content set,
//  else start with initial default values by productFamily

const relatedResourceCollectionAdapter = createEntityAdapter({
    sortComparer: (b, a) => {
        if (b?.resourceType && a?.resourceType && b.resourceType !== a.resourceType) {
            return b.resourceType.localeCompare(a.resourceType)
        } else if (b?.displayTitle && a?.displayTitle && b.displayTitle !== a.displayTitle) {
            return b.displayTitle.localeCompare(a.displayTitle)
        } else if (b?.id && a?.id && b.id !== a.id) {
            return b.id.localeCompare(a.id)
        } else {
            return 0
        }
    }
})

const initialState = relatedResourceCollectionAdapter.getInitialState({
    status: 'idle',
    error: null
})

const relatedResourceCollectionSlice = createAppSlice({
    name: "relatedResourceCollection",
    initialState,
    reducers: create => ({
        resourceAdded: create.preparedReducer(
            (resourceBody) => {
                return {
                    payload: {
                        id: resourceBody.id,
                        displayTitle: resourceBody.displayTitle,
                        productFamily: resourceBody.productFamily,
                        parentProductFOI: resourceBody.parentProductFOI,
                        resourceType: resourceBody.resourceType,
                        resourceJson: resourceBody.resourceJson,
                        revisedResourceJson: resourceBody.resourceJson,
                        usageData: resourceBody.usageData,
                        invitecode: resourceBody.invitecode || ""
                    }
                }
            },
            relatedResourceCollectionAdapter.addOne
        ),
        resourceUpdated: create.reducer((state, action) => {
            const { id, displayTitle, productFamily, parentProductFOI, resourceType, resourceJson, revisedResourceJson, usageData, invitecode } = action.payload;
            relatedResourceCollectionAdapter.updateOne(state,
                { id, changes: { displayTitle, productFamily, parentProductFOI, resourceType, resourceJson, revisedResourceJson, usageData, invitecode } })
        }),
        usageDataUpdated: create.reducer((state, action) => {
            // The payload should be an object like: { id: "some-id", usageData: { ... } }
            const { id, usageData } = action.payload;
            relatedResourceCollectionAdapter.updateOne(state, {
                id,
                changes: { usageData } // This will only update the usageData field
            });
        }),
        inviteCodeUpdated: create.reducer((state, action) => {
            // The payload should be: { id: "some-id", inviteCode: "new-code-xyz" }
            const { id, inviteCode } = action.payload;

            // Find the specific resource entity in the state
            const existingResource = state.entities[id];

            // If the resource exists, update its nested property
            if (existingResource) {
                existingResource.invitecode = inviteCode;
            }
        }),
        fetchRelatedResourceCollection: create.asyncThunk(
            async (body, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const resourceEntities = await fetchResourceCollectionAPI(body.parentProductFoi, body.productFamily, apiToken, body.resourceType, body.fhirJson);
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { resourceCollection } = thunkApi.getState()
                         if (resourceCollection.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    relatedResourceCollectionAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectRelatedResourceCollectionStatus: relatedResourceCollection => relatedResourceCollection.status,
        selectRelatedResourceCollectionError: relatedResourceCollection => relatedResourceCollection.error
    },
})

export const { resourceAdded, resourceUpdated, usageDataUpdated, inviteCodeUpdated, fetchRelatedResourceCollection } = relatedResourceCollectionSlice.actions

export const { selectRelatedResourceCollectionStatus, selectRelatedResourceCollectionError } = relatedResourceCollectionSlice.selectors

export const {
    selectAll: selectAllRelatedResources,
    selectById: selectRelatedResourceById,
} = relatedResourceCollectionAdapter.getSelectors(state => state.relatedResourceCollection)

export default relatedResourceCollectionSlice